import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaSearch, FaSyncAlt, FaDownload, FaInfoCircle, FaShieldAlt, FaHistory, FaCalendarDay,
  FaPlusCircle, FaEdit, FaTrash, FaSignature, FaKey, FaExclamationTriangle, FaCheckCircle, FaTimesCircle,
} from 'react-icons/fa';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { auditAPI } from '../services/api';
import { stampPdfFooter } from '../utils/reportProfiles';
import { Modal } from '../components/Modals';
import TablePagination from '../components/TablePagination';
import { StatCard } from '../components/UnifiedUIComponents';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

// Summary cards: each one is a server-side filter on the trail.
const CARDS = [
  { key: 'all', title: 'Total Records', icon: FaHistory, color: 'primary', count: 'total', hint: 'Show all audit records' },
  { key: 'today', title: 'Today', icon: FaCalendarDay, color: 'info', count: 'today', hint: 'Filter: events recorded today' },
  { key: 'create', title: 'Created', icon: FaPlusCircle, color: 'success', count: 'creates', hint: 'Filter: create / import events' },
  { key: 'update', title: 'Updated', icon: FaEdit, color: 'warning', count: 'updates', hint: 'Filter: update / edit events' },
  { key: 'delete', title: 'Deleted', icon: FaTrash, color: 'danger', count: 'deletes', hint: 'Filter: delete / deactivate events' },
  { key: 'approval', title: 'Approvals & Sign-offs', icon: FaSignature, color: 'primary', count: 'approvals', hint: 'Filter: approval, rejection and e-signature events' },
  { key: 'security', title: 'Login & Security', icon: FaKey, color: 'info', count: 'security', hint: 'Filter: login, logout and password events' },
  { key: 'failed', title: 'Failed Events', icon: FaExclamationTriangle, color: 'danger', count: 'failed', hint: 'Filter: unsuccessful events (e.g. failed logins)' },
];

const GROUP_STYLES = {
  create: { background: 'var(--info-bg)', color: 'var(--info-text)' },
  update: { background: 'var(--warning-bg)', color: 'var(--warning-text)' },
  delete: { background: 'var(--danger-bg)', color: 'var(--danger-text)' },
  approval: { background: 'var(--success-bg)', color: 'var(--success-text)' },
  security: { background: 'var(--surface-light)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)' },
  other: { background: 'var(--surface-light)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)' },
};

// table_name (or, when a module logs without one, its MODULE name) -> the screen that
// owns the record. Values here are the real `table_name`/`module` strings audit.js emits
// (see backend/server.js auditWrites() mounts and each route's own logAudit calls) — matching
// against guessed keywords instead of these exact names silently sends every record to the
// wrong screen (e.g. "training" matched "training_name" filters too). Where the target page
// already understands a `openViewXId` deep-link (Results.js, EquipmentMaster.js,
// EquipmentTrain.js, ProductMaster.js), pass it in `state` so the specific record opens
// rather than just the list.
const ENTITY_ROUTES = [
  [/^users?$/i, () => ({ path: '/users' })],
  [/^departments?$/i, () => ({ path: '/users' })],
  [/^API_MASTER$/i, () => ({ path: '/api-master' })],
  [/^product_master$/i, (id) => ({ path: '/product-master', state: id ? { openViewProductId: id } : undefined })],
  [/^product_train_links$/i, () => ({ path: '/product-master' })],
  [/^product_risk_assessments$/i, () => ({ path: '/risk-assessment' })],
  [/^equipment_master$/i, (id) => ({ path: '/equipment-master', state: id ? { openViewEquipmentId: id } : undefined })],
  [/^equipment_parts$/i, () => ({ path: '/equipment-master' })],
  [/^equipment_train$/i, (id) => ({ path: '/equipment-trains', state: id ? { openViewTrainId: id } : undefined })],
  [/^equipment_train_members$/i, () => ({ path: '/equipment-trains' })],
  [/^worst_case_decisions$/i, () => ({ path: '/worst-case' })],
  [/^maco_calculations$/i, () => ({ path: '/maco' })],
  [/^sampling_plans$/i, () => ({ path: '/sampling-plans' })],
  [/^results?$/i, (id) => ({ path: '/results', state: id ? { openViewResultId: id } : undefined })],
  [/^ccv_plans$|^ccv_results$/i, () => ({ path: '/ccv' })],
  [/^revalidation_triggers$/i, () => ({ path: '/revalidation' })],
  [/^training_records$|^training_master$/i, () => ({ path: '/training' })],
  [/^vrl_studies$/i, () => ({ path: '/vrl' })],
  [/^equipment_cleaning_config$/i, () => ({ path: '/cleaning-config' })],
  [/^detergent_master$/i, () => ({ path: '/cleaning-config' })],
  [/^ac_solvent_criteria$|^ac_area_classification$|^ac_equipment_criteria$/i, () => ({ path: '/acceptance-criteria' })],
  [/^analytical_method_recovery$/i, () => ({ path: '/analytical-methods' })],
  [/^units?$|^production_lines$|^dosage_forms$/i, () => ({ path: '/commercial-setup' })],
  [/^documents$/i, () => ({ path: '/documents' })],
  [/^cv_documents$/i, () => ({ path: '/study-report' })],
  [/^APPROVAL_FLOW$|^approval_chains$/i, () => ({ path: '/settings?tab=approval' })],
  [/^app_integrations$/i, () => ({ path: '/integrations' })],
  [/^NOTIFICATIONS$/i, () => ({ path: '/settings?tab=alerts' })],
  [/^SYSTEM_SETTINGS$/i, () => ({ path: '/settings' })],
  [/^AI/i, () => ({ path: '/ai-assistant' })],
];
const entityPath = (entry) => {
  const type = String(entry?.entityType || '');
  const match = ENTITY_ROUTES.find(([re]) => re.test(type));
  return match ? match[1](entry.entityId) : null;
};

const humanize = (key) => String(key).replace(/_/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
const fmtValue = (v) => {
  if (v === undefined) return '—';
  if (v === null) return 'null';
  if (Array.isArray(v)) return v.length ? v.map((x) => (typeof x === 'object' && x !== null ? JSON.stringify(x) : String(x))).join(', ') : '(empty)';
  if (typeof v === 'object') return JSON.stringify(v);
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
};

// Field-level diff of the old/new snapshots. When only one side exists every field is added/removed.
const buildDiff = (oldValues, newValues) => {
  const o = oldValues || {};
  const n = newValues || {};
  const rows = [];
  [...new Set([...Object.keys(o), ...Object.keys(n)])].forEach((k) => {
    const hasOld = Object.prototype.hasOwnProperty.call(o, k);
    const hasNew = Object.prototype.hasOwnProperty.call(n, k);
    const oldStr = fmtValue(hasOld ? o[k] : undefined);
    const newStr = fmtValue(hasNew ? n[k] : undefined);
    let type = 'changed';
    if (!oldValues) type = 'added';
    else if (!newValues) type = 'removed';
    else if (!hasOld) type = 'added';
    else if (!hasNew) type = 'removed';
    else if (oldStr === newStr) return;
    rows.push({ key: k, label: humanize(k), oldStr, newStr, type });
  });
  return rows;
};

const DIFF_BG = { added: 'var(--success-bg)', removed: 'var(--danger-bg)', changed: 'var(--warning-bg)' };

const DiffTable = ({ oldValues, newValues }) => {
  const rows = buildDiff(oldValues, newValues);
  if (!rows.length) return <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-sm)' }}>No field-level changes recorded for this entry.</div>;
  return (
    <div style={{ maxHeight: '300px', overflow: 'auto', border: '1px solid var(--border-color)', borderRadius: '6px' }}>
      <table data-no-enhance style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-sm)' }}>
        <thead>
          <tr>
            {['Field', 'Old Value', 'New Value'].map((h) => <th key={h} style={{ textAlign: 'left', padding: '6px 10px', position: 'sticky', top: 0, background: 'var(--card-bg)' }}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} style={{ background: DIFF_BG[r.type] }}>
              <td style={{ padding: '6px 10px', fontWeight: 600, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{r.label}</td>
              <td style={{ padding: '6px 10px', wordBreak: 'break-word', verticalAlign: 'top', textDecoration: r.type === 'removed' ? 'line-through' : 'none' }}>{r.type === 'added' && !oldValues ? '—' : r.oldStr}</td>
              <td style={{ padding: '6px 10px', wordBreak: 'break-word', verticalAlign: 'top' }}>{r.type === 'removed' && !newValues ? '—' : r.newStr}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const fmtTs = (v) => (v ? new Date(v).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'medium' }) : '—');
const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
const dateInputToIso = (value, endOfDay) => (value ? new Date(`${value}T${endOfDay ? '23:59:59.999' : '00:00:00'}`).toISOString() : '');

const EMPTY_FILTERS = { search: '', action: '', status: '', card: 'all', fromDate: '', toDate: '', entityType: '', entityId: '' };

const changeKind = (e) => {
  if (!e.oldValues && !e.newValues) return null;
  if (!e.oldValues) return { label: 'Added', style: GROUP_STYLES.create };
  if (!e.newValues) return { label: 'Removed', style: GROUP_STYLES.delete };
  return { label: 'Changed', style: GROUP_STYLES.update };
};

const badgeStyle = { padding: '2px 9px', borderRadius: '999px', fontSize: 'var(--fs-xs)', fontWeight: 700, whiteSpace: 'nowrap' };

const AuditTrail = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const seededType = searchParams.get('entityType') || '';
  const seededId = searchParams.get('entityId') || '';

  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [denied, setDenied] = useState(false);
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS, entityType: seededType, entityId: seededId });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [actions, setActions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selected, setSelected] = useState(null);
  const [sort, setSort] = useState({ key: 'timestamp', dir: 'desc' });
  const [integrity, setIntegrity] = useState(null); // null | { checking } | result
  const searchTimer = useRef(null);
  const requestSeq = useRef(0);

  // Translate UI filters (incl. the active summary card) into API params.
  const toParams = useCallback((f) => {
    const params = {};
    if (f.search) params.search = f.search;
    if (f.action) params.action = f.action;
    if (f.status) params.status = f.status;
    if (f.entityType) params.entityType = f.entityType;
    if (f.entityId) params.entityId = f.entityId;
    if (['create', 'update', 'delete', 'approval', 'security'].includes(f.card)) params.actionGroup = f.card;
    if (f.card === 'failed') params.status = 'FAILURE';
    const from = f.card === 'today' ? startOfToday().toISOString() : dateInputToIso(f.fromDate, false);
    if (from) params.from = from;
    const to = dateInputToIso(f.toDate, true);
    if (to) params.to = to;
    return params;
  }, []);

  const load = useCallback(async (pg, f, size) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const res = await auditAPI.getTrail({ ...toParams(f), page: pg, pageSize: size });
      if (seq !== requestSeq.current) return; // a newer request superseded this one
      const data = res?.data?.data || {};
      setEntries(data.entries || []);
      setTotal(data.total || 0);
      setDenied(false);
    } catch (error) {
      if (seq !== requestSeq.current) return;
      if (error?.response?.status === 403) setDenied(true);
      else toast.error('Failed to load audit trail');
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [toParams]);

  const loadSummary = useCallback(async () => {
    try {
      const res = await auditAPI.getSummary();
      setSummary(res?.data?.data || null);
    } catch (error) { /* cards simply stay empty */ }
  }, []);

  useEffect(() => {
    load(1, filters, pageSize);
    loadSummary();
    auditAPI.getActions().then((res) => setActions(res?.data?.data || [])).catch(() => {});
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // The deep-link seed (entityType/entityId) is consumed once, so a refresh doesn't re-lock the filter.
  useEffect(() => {
    if (!seededType && !seededId) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('entityType');
      next.delete('entityId');
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = (patch) => {
    const next = { ...filters, ...patch };
    setFilters(next);
    setPage(1);
    load(1, next, pageSize);
  };

  const handleSearchInput = (value) => {
    const next = { ...filters, search: value };
    setFilters(next);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setPage(1); load(1, next, pageSize); }, 400);
  };

  const goToPage = (p) => {
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const target = Math.min(totalPages, Math.max(1, p));
    setPage(target);
    load(target, filters, pageSize);
  };

  const resetAll = () => {
    setFilters({ ...EMPTY_FILTERS });
    setPage(1);
    load(1, EMPTY_FILTERS, pageSize);
    loadSummary();
  };

  const toggleCard = (key) => applyFilters({ card: filters.card === key || key === 'all' ? 'all' : key, status: key === 'failed' ? '' : filters.status });

  const runVerify = async () => {
    setIntegrity({ checking: true });
    try {
      const res = await auditAPI.verify();
      setIntegrity(res?.data?.data || null);
    } catch (error) {
      setIntegrity(null);
      toast.error(error?.response?.data?.message || 'Integrity verification failed to run');
    }
  };

  const hasActiveFilters = filters.search || filters.action || filters.status || filters.card !== 'all'
    || filters.fromDate || filters.toDate || filters.entityType || filters.entityId;

  const sortedEntries = useMemo(() => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    const val = (e) => {
      switch (sort.key) {
        case 'user': return e.userName || '';
        case 'action': return e.action || '';
        case 'entity': return `${e.entityType || ''} ${e.entityId || ''}`;
        case 'status': return e.status || '';
        default: return new Date(e.timestamp || 0).getTime();
      }
    };
    return [...entries].sort((a, b) => {
      const av = val(a); const bv = val(b);
      if (typeof av === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv), undefined, { numeric: true }) * dir;
    });
  }, [entries, sort]);

  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'timestamp' ? 'desc' : 'asc' }));
  const sortMark = (key) => (sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '');

  const openRelated = (entry) => {
    const target = entityPath(entry);
    if (target) navigate(target.path, target.state ? { state: target.state } : undefined);
    else toast.info('No screen is mapped to this record type.');
  };

  // ── Export ──────────────────────────────────────────────────────────────────
  const exportMeta = () => {
    let user = null;
    try { user = JSON.parse(localStorage.getItem('user') || 'null'); } catch (error) { user = null; }
    const p = toParams(filters);
    return {
      exportedAt: new Date(),
      exportedBy: `${user?.username || 'Unknown'}${user?.role ? ` (${user.role})` : ''}`,
      filters: [
        filters.search && `Search: ${filters.search}`,
        filters.action && `Action: ${filters.action}`,
        p.actionGroup && `Category: ${p.actionGroup}`,
        p.status && `Status: ${p.status}`,
        filters.card === 'today' && 'Period: today',
        filters.fromDate && `From: ${filters.fromDate}`,
        filters.toDate && `To: ${filters.toDate}`,
        filters.entityType && `Entity: ${filters.entityType}${filters.entityId ? ` #${filters.entityId}` : ''}`,
      ].filter(Boolean).join(' | ') || 'None (all records)',
    };
  };

  const exportRows = (list) => list.map((e) => ({
    'Audit ID': e.auditId,
    'Seq': e.chainSeq ?? '',
    'Timestamp': fmtTs(e.timestamp),
    'User': e.userName,
    'Role': e.userRole || '',
    'Action': e.action,
    'Entity': e.entityType || '',
    'Entity ID': e.entityId || '',
    'Description': e.description || '',
    'Status': e.status,
    'IP Address': e.ipAddress || '',
    'Record Hash': e.rowHash || '',
  }));

  const fetchForExport = async (limit) => {
    const res = await auditAPI.getTrail({ ...toParams(filters), page: 1, pageSize: limit });
    return res?.data?.data?.entries || [];
  };

  const recordExport = (format, count) => {
    auditAPI.logExport({ format, count, filters: toParams(filters) }).then(loadSummary).catch(() => {});
  };

  const exportExcel = async () => {
    if (!total) return toast.warning('No data to export');
    try {
      const list = await fetchForExport(5000);
      const meta = exportMeta();
      const rows = exportRows(list);
      const header = [
        ['Audit Trail — 21 CFR Part 11 §11.10(e)'],
        [`Exported: ${meta.exportedAt.toLocaleString()}`, `By: ${meta.exportedBy}`],
        [`Filters: ${meta.filters}`],
        [`Records: ${list.length} of ${total}`],
        [],
      ];
      const ws = XLSX.utils.aoa_to_sheet(header);
      XLSX.utils.sheet_add_json(ws, rows, { origin: header.length });
      ws['!cols'] = [12, 6, 22, 16, 14, 22, 18, 10, 60, 10, 16, 66].map((wch) => ({ wch }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Audit Trail');
      XLSX.writeFile(wb, `AuditTrail_${meta.exportedAt.toISOString().slice(0, 16).replace(/[-:T]/g, '')}.xlsx`);
      recordExport('xlsx', list.length);
      if (total > list.length) toast.info(`Exported the latest ${list.length} of ${total} records — narrow the filters to export the rest.`);
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const exportPdf = async () => {
    if (!total) return toast.warning('No data to export');
    try {
      const list = await fetchForExport(1000);
      const meta = exportMeta();
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      doc.setFontSize(14);
      doc.text('Audit Trail — 21 CFR Part 11 §11.10(e)', 40, 38);
      doc.setFontSize(8);
      doc.text(`Exported ${meta.exportedAt.toLocaleString()} by ${meta.exportedBy}`, 40, 54);
      doc.text(`Filters: ${meta.filters}`, 40, 66);
      doc.text(`Records: ${list.length} of ${total}`, 40, 78);
      doc.autoTable({
        startY: 88,
        head: [['Audit ID', 'Timestamp', 'User', 'Action', 'Entity', 'Description', 'Status', 'IP']],
        body: list.map((e) => [
          e.auditId, fmtTs(e.timestamp), e.userName, e.action,
          `${e.entityType || ''}${e.entityId ? ` #${e.entityId}` : ''}`, e.description || '', e.status, e.ipAddress || '',
        ]),
        styles: { fontSize: 7, cellPadding: 3, overflow: 'linebreak' },
        headStyles: { fillColor: [55, 65, 81] },
        columnStyles: { 5: { cellWidth: 240 } },
        didDrawPage: () => {
          const w = doc.internal.pageSize.getWidth();
          const h = doc.internal.pageSize.getHeight();
          doc.setFontSize(7);
          doc.text(`Page ${doc.internal.getNumberOfPages()}`, w - 60, h - 20);
          doc.text('System-generated, read-only record. Tamper-evident (SHA-256 hash chain).', 40, h - 20);
        },
      });
      stampPdfFooter(doc, 'audit_trail', { pageNumbers: false });
      doc.save(`AuditTrail_${meta.exportedAt.toISOString().slice(0, 16).replace(/[-:T]/g, '')}.pdf`);
      recordExport('pdf', list.length);
      if (total > list.length) toast.info(`Exported the latest ${list.length} of ${total} records — narrow the filters to export the rest.`);
    } catch (error) {
      toast.error('Export failed');
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  if (denied) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
        <FaShieldAlt style={{ fontSize: 'var(--fs-3xl)', marginBottom: '12px', opacity: 0.5 }} />
        <div style={{ fontSize: 'var(--fs-lg)', fontWeight: 600 }}>Audit Trail access is restricted</div>
        <p>Only Admin, Super Admin and QA roles can review the audit trail.</p>
      </div>
    );
  }

  const selectedChange = selected ? changeKind(selected) : null;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 className="page-title" style={{ margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <FaShieldAlt style={{ color: 'var(--primary-color)' }} /> Audit Trail
            <span style={{ ...badgeStyle, background: 'var(--info-bg)', color: 'var(--info-text)' }}>21 CFR Part 11 §11.10(e)</span>
          </h2>
          <p className="page-subtitle">
            Read-only · Immutable · Hash-chained · {(summary?.total ?? total).toLocaleString()} records — system-generated, time-stamped log of record changes and user actions.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button className="btn btn-outline" onClick={runVerify} disabled={integrity?.checking} title="Recompute the SHA-256 hash chain to prove no record was altered or removed">
            <FaShieldAlt /> {integrity?.checking ? 'Verifying…' : 'Verify Integrity'}
          </button>
          <button className="btn btn-success" onClick={exportExcel}><FaDownload /> Excel</button>
          <button className="btn btn-danger" onClick={exportPdf}><FaDownload /> PDF</button>
        </div>
      </div>

      {integrity && !integrity.checking && (
        <div
          role="status"
          style={{
            display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '14px', padding: '10px 14px', borderRadius: '8px',
            background: integrity.intact ? 'var(--success-bg)' : 'var(--danger-bg)',
            color: integrity.intact ? 'var(--success-text)' : 'var(--danger-text)',
            border: `1px solid ${integrity.intact ? 'var(--success-border)' : 'var(--danger-border)'}`,
          }}
        >
          {integrity.intact ? <FaCheckCircle /> : <FaTimesCircle />}
          <strong>{integrity.intact ? 'Audit trail integrity verified' : 'INTEGRITY CHECK FAILED'}</strong>
          <span style={{ fontSize: 'var(--fs-sm)' }}>
            {integrity.intact
              ? `${integrity.checked.toLocaleString()} records checked — no record has been altered or removed. Verified ${new Date(integrity.verifiedAt).toLocaleString()} by ${integrity.verifiedBy}.`
              : `Break detected at record ${integrity.firstBrokenId ? `AUD-${String(integrity.firstBrokenId).padStart(8, '0')}` : `#${integrity.firstBrokenSeq}`} (sequence ${integrity.firstBrokenSeq}): ${integrity.reason}. Escalate to QA / IT immediately.`}
          </span>
        </div>
      )}

      <div className="stat-card-grid">
        {CARDS.map((c) => (
          <StatCard
            key={c.key} compact filterHint title={c.title} icon={c.icon} color={c.color} hint={c.hint}
            value={summary ? (summary[c.count] ?? 0).toLocaleString() : '—'}
            active={filters.card === c.key}
            onClick={() => toggleCard(c.key)}
          />
        ))}
      </div>

      {(filters.entityType || filters.entityId) && (
        <div style={{ marginBottom: '10px' }}>
          <span style={{ ...badgeStyle, background: 'var(--info-bg)', color: 'var(--info-text)', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            Filtered to: {filters.entityType}{filters.entityId ? ` #${filters.entityId}` : ''}
            <button type="button" aria-label="Clear entity filter" onClick={() => applyFilters({ entityType: '', entityId: '' })} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'inherit', fontWeight: 700 }}>×</button>
          </span>
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 240px' }}>
          <FaSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)', fontSize: 'var(--fs-md)' }} />
          <input className="form-control form-control-sm" style={{ paddingLeft: '30px' }} placeholder="Search user, action, description, entity, IP..." value={filters.search} onChange={(e) => handleSearchInput(e.target.value)} />
        </div>
        <select className="form-control form-control-sm" style={{ width: 'auto', maxWidth: '200px' }} value={filters.action} onChange={(e) => applyFilters({ action: e.target.value })} title="Filter by action">
          <option value="">All actions</option>
          {actions.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select className="form-control form-control-sm" style={{ width: 'auto' }} value={filters.status} onChange={(e) => applyFilters({ status: e.target.value, card: filters.card === 'failed' ? 'all' : filters.card })} title="Filter by outcome">
          <option value="">All outcomes</option>
          <option value="SUCCESS">Success</option>
          <option value="FAILURE">Failure</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
          From
          <input type="date" className="form-control form-control-sm" style={{ width: 'auto' }} value={filters.fromDate} max={filters.toDate || undefined} onChange={(e) => applyFilters({ fromDate: e.target.value, card: filters.card === 'today' ? 'all' : filters.card })} />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
          To
          <input type="date" className="form-control form-control-sm" style={{ width: 'auto' }} value={filters.toDate} min={filters.fromDate || undefined} onChange={(e) => applyFilters({ toDate: e.target.value })} />
        </label>
        <button className="btn btn-outline btn-sm" onClick={resetAll} disabled={!hasActiveFilters}><FaSyncAlt /> Reset</button>
      </div>

      {entries.length === 0 && !loading ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
          <FaHistory style={{ fontSize: 'var(--fs-3xl)', marginBottom: '12px', opacity: 0.4 }} />
          <div style={{ fontSize: 'var(--fs-lg)', fontWeight: 600 }}>No audit records found</div>
          {hasActiveFilters && <div style={{ marginTop: '6px' }}>Try clearing the filters.</div>}
        </div>
      ) : (
        <div className="table-container" style={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.15s' }}>
          <table>
            <thead>
              <tr>
                <th onClick={() => toggleSort('timestamp')} style={{ cursor: 'pointer' }}>Timestamp{sortMark('timestamp')}</th>
                <th onClick={() => toggleSort('user')} style={{ cursor: 'pointer' }}>User{sortMark('user')}</th>
                <th onClick={() => toggleSort('action')} style={{ cursor: 'pointer' }}>Action{sortMark('action')}</th>
                <th onClick={() => toggleSort('entity')} style={{ cursor: 'pointer' }}>Entity{sortMark('entity')}</th>
                <th>Description</th>
                <th onClick={() => toggleSort('status')} style={{ cursor: 'pointer' }}>Status{sortMark('status')}</th>
                <th>IP Address</th>
                <th>Changes</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {sortedEntries.map((e) => {
                const change = changeKind(e);
                const path = entityPath(e);
                return (
                  <tr key={e.id}>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 'var(--fs-sm)' }}>{fmtTs(e.timestamp)}</td>
                    <td>
                      <div style={{ fontWeight: 600, fontSize: 'var(--fs-sm)' }}>{e.userName}</div>
                      {e.userRole && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>{e.userRole}</div>}
                    </td>
                    <td><span style={{ ...badgeStyle, ...(GROUP_STYLES[e.actionGroup] || GROUP_STYLES.other) }}>{e.action}</span></td>
                    <td>
                      {e.entityType ? (
                        path
                          ? <button type="button" className="btn btn-link btn-sm" style={{ padding: 0 }} onClick={() => openRelated(e)}>{e.entityType}{e.entityId ? ` #${e.entityId}` : ''}</button>
                          : <span>{e.entityType}{e.entityId ? ` #${e.entityId}` : ''}</span>
                      ) : '—'}
                    </td>
                    <td style={{ whiteSpace: 'normal', wordBreak: 'break-word', fontSize: 'var(--fs-sm)', minWidth: '220px' }} title={e.description}>{e.description}</td>
                    <td>
                      <span style={{ ...badgeStyle, ...(e.status === 'SUCCESS' ? GROUP_STYLES.approval : GROUP_STYLES.delete) }}>{e.status}</span>
                    </td>
                    <td style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>{e.ipAddress || '—'}</td>
                    <td>{change ? <span style={{ ...badgeStyle, ...change.style }}>{change.label}</span> : '—'}</td>
                    <td style={{ textAlign: 'center' }}>
                      <button type="button" className="btn btn-outline btn-sm" onClick={() => setSelected(e)} title="View full audit entry"><FaInfoCircle /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <TablePagination
        total={total}
        page={page}
        pageSize={pageSize}
        onPageChange={goToPage}
        onPageSizeChange={(size) => { setPageSize(size); setPage(1); load(1, filters, size); }}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        itemLabel="records"
      />

      <Modal
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        title="Audit Entry Detail"
        size="lg"
        footer={(
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            {selected && entityPath(selected) && <button className="btn btn-outline" onClick={() => { openRelated(selected); setSelected(null); }}>Open Related Record</button>}
            <button className="btn btn-primary" onClick={() => setSelected(null)}>Close</button>
          </div>
        )}
      >
        {selected && (
          <div>
            <table data-no-enhance style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-md)', marginBottom: '14px' }}>
              <tbody>
                {[
                  ['Audit ID', selected.auditId],
                  ['Timestamp', `${fmtTs(selected.timestamp)}${selected.timestamp ? ` (${new Date(selected.timestamp).toISOString()})` : ''}`],
                  ['User', `${selected.userName}${selected.userRole ? ` — ${selected.userRole}` : ''}${selected.userId ? ` (ID ${selected.userId})` : ''}`],
                  ['Action', <span key="a" style={{ ...badgeStyle, ...(GROUP_STYLES[selected.actionGroup] || GROUP_STYLES.other) }}>{selected.action}</span>],
                  ['Entity', selected.entityType ? `${selected.entityType}${selected.entityId ? ` #${selected.entityId}` : ''}` : '—'],
                  ['Description', selected.description || '—'],
                  ['Status', <span key="s" style={{ ...badgeStyle, ...(selected.status === 'SUCCESS' ? GROUP_STYLES.approval : GROUP_STYLES.delete) }}>{selected.status}</span>],
                  ['IP Address', selected.ipAddress || '—'],
                  ['User Agent', selected.userAgent || '—'],
                ].map(([label, value]) => (
                  <tr key={label}>
                    <td style={{ padding: '6px 10px', width: '150px', fontWeight: 600, color: 'var(--text-secondary)', verticalAlign: 'top', borderBottom: '1px solid var(--border-color)' }}>{label}</td>
                    <td style={{ padding: '6px 10px', wordBreak: 'break-word', borderBottom: '1px solid var(--border-color)' }}>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {(selected.oldValues || selected.newValues) && (
              <div style={{ marginBottom: '14px' }}>
                <div style={{ fontWeight: 700, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  Changes {selectedChange && <span style={{ ...badgeStyle, ...selectedChange.style }}>{selectedChange.label}</span>}
                </div>
                <DiffTable oldValues={selected.oldValues} newValues={selected.newValues} />
                <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)', marginTop: '4px' }}>Sensitive fields (passwords, tokens, keys) are redacted from this view.</div>
              </div>
            )}

            <div style={{ fontWeight: 700, marginBottom: '6px' }}>Tamper-evidence</div>
            <table data-no-enhance style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-sm)' }}>
              <tbody>
                {[['Chain sequence', selected.chainSeq ?? '—'], ['Record hash (SHA-256)', selected.rowHash || '—'], ['Previous record hash', selected.prevHash || '— (first record)']].map(([label, value]) => (
                  <tr key={label}>
                    <td style={{ padding: '4px 10px', width: '170px', color: 'var(--text-secondary)', verticalAlign: 'top' }}>{label}</td>
                    <td style={{ padding: '4px 10px', wordBreak: 'break-all', fontFamily: 'monospace' }}>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AuditTrail;
